/**
 * ResumePreviewPanel component wraps ResumePreview with:
 * - Header with template selector
 * - Scrollable preview area
 * - Footer with Generate button
 */

import { cn } from '@/lib/utils'
import type { ResumeContent, TemplateId } from '@/types'
import { FileDown, FileText, Loader2, X } from 'lucide-react'
import { AgentPdfDocumentViewer } from './agent/agent-pdf-document-viewer'
import { ResumePreview } from './resume-preview'
import { Button } from './ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'

interface ResumePreviewPanelProps {
  content: ResumeContent | null
  originalContent?: ResumeContent | null // From parsed_resume_data
  template: TemplateId
  onTemplateChange: (template: TemplateId) => void
  onGenerate: () => void
  onClose?: () => void
  isGenerating: boolean
  isUpdatingContent?: boolean // Shows overlay while AI is generating new content
  jobTitle?: string | undefined // Reserved for future tailored context (unused in chrome)
  company?: string | undefined
  pdfUrl?: string | null
  isLoading?: boolean
  isAgentTailored?: boolean
  onDownload?: (() => void) | undefined
  isDownloading?: boolean
  className?: string
}

// Same 6 templates the Export panel and builder switcher expose
// (resume-export-panel.tsx's TEMPLATE_OPTIONS / resume-builder.tsx's
// BUILDER_TEMPLATES) -- this picker only had Modern/Executive, a subset left
// over from before the other 4 templates were added elsewhere. "pikachu"
// stays excluded from all three pickers (predates that pass, was never
// exposed in any of them).
const templateOptions = [
  { value: 'modern', label: 'Modern' },
  { value: 'executive', label: 'Executive' },
  { value: 'two_column_photo', label: 'Two-Column' },
  { value: 'bold_creative', label: 'Bold Creative' },
  { value: 'academic_cv', label: 'Academic CV' },
  { value: 'timeline', label: 'Timeline' },
] as const

export function ResumePreviewPanel({
  content,
  originalContent,
  template,
  onTemplateChange,
  onGenerate,
  onClose,
  isGenerating,
  isUpdatingContent,
  jobTitle: _jobTitle,
  company: _company,
  pdfUrl,
  isLoading = false,
  isAgentTailored = false,
  onDownload,
  isDownloading = false,
  className,
}: ResumePreviewPanelProps) {
  // Show the current content, or fall back to original content, or null
  const displayContent = content || originalContent
  const showPdfFallback = !displayContent && Boolean(pdfUrl)
  const showTemplateToggle = Boolean(displayContent)
  const useDownloadAction = isAgentTailored && Boolean(onDownload)

  return (
    <div className={cn('h-full flex flex-col bg-gray-50 relative', className)}>
      {/* Overlay while AI is updating content */}
      {isUpdatingContent && (
        <div className="absolute inset-0 z-10 overflow-hidden">
          {/* Frosted glass overlay */}
          <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px]" />

          {/* Scanning line effect */}
          <div
            className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-indigo-400 to-transparent opacity-60"
            style={{ animation: 'scan 2s ease-in-out infinite' }}
          />

          {/* Center indicator */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              {/* Pulsing rings */}
              <div
                className="absolute inset-0 rounded-full border-2 border-indigo-400/30"
                style={{
                  width: '80px',
                  height: '80px',
                  left: '-20px',
                  top: '-20px',
                  animation: 'pulse-ring 1.5s ease-out infinite',
                }}
              />
              <div
                className="absolute inset-0 rounded-full border-2 border-indigo-400/20"
                style={{
                  width: '80px',
                  height: '80px',
                  left: '-20px',
                  top: '-20px',
                  animation: 'pulse-ring 1.5s ease-out infinite 0.5s',
                }}
              />
              {/* Center icon */}
              <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                <svg
                  className="w-5 h-5 text-white"
                  style={{ animation: 'spin 2s linear infinite' }}
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes scan {
              0% { top: 0%; opacity: 0; }
              10% { opacity: 0.6; }
              90% { opacity: 0.6; }
              100% { top: 100%; opacity: 0; }
            }
            @keyframes pulse-ring {
              0% { transform: scale(0.8); opacity: 0.8; }
              100% { transform: scale(1.5); opacity: 0; }
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Header with template toggle */}
      <div className="px-4 py-3 border-b bg-white flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0 shrink-0">
          <FileText className="h-4 w-4 text-[#2563eb] shrink-0" />
          <h3 className="text-sm font-semibold text-[#0f172a] truncate">Resume Preview</h3>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          {showTemplateToggle ? (
            // A dropdown, not the pill row this used to be -- 6 template
            // options (up from the original 2) made the pill row overflow
            // this panel's width when it's docked in a narrow sidebar (see
            // app-layout.tsx).
            <Select
              value={template}
              onValueChange={(value) => onTemplateChange(value as TemplateId)}
            >
              <SelectTrigger className="h-8 w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {templateOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 hover:bg-slate-100 rounded-full transition-colors"
              title="Close preview"
            >
              <X className="h-4 w-4 text-slate-400" />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable preview area */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-full min-h-[320px] items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading preview…
          </div>
        ) : displayContent ? (
          <div className="p-4">
            {/* Center the resume in the panel, matching pre-migration
                builds. Letter/A4 width ≈ 816px; max-w-3xl (768px) leaves
                a small breathing margin on wider panels without
                truncating the page width. */}
            <div className="mx-auto w-full max-w-3xl">
              <div className="bg-white shadow-sm border rounded-lg overflow-hidden">
                <ResumePreview content={displayContent} template={template} />
              </div>
              {!content && originalContent && !isAgentTailored && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  This is your original resume. Ask the AI to improve it!
                </p>
              )}
            </div>
          </div>
        ) : showPdfFallback && pdfUrl ? (
          <div className="p-4">
            <div className="mx-auto w-full max-w-3xl">
              <AgentPdfDocumentViewer url={pdfUrl} title="Tailored resume" />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <FileText className="h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500">Upload a resume or ask the AI to improve it</p>
            <p className="text-sm text-gray-400 mt-2">Your resume will appear here for preview</p>
          </div>
        )}
      </div>

      {/* Footer with Generate / Download buttons */}
      <div className="p-4 border-t bg-[#eef2f7] shrink-0">
        <Button
          onClick={useDownloadAction ? onDownload : onGenerate}
          disabled={
            useDownloadAction
              ? isDownloading || isLoading || (!displayContent && !showPdfFallback)
              : !displayContent || isGenerating
          }
          className="w-full rounded-full bg-[#2563eb] text-white hover:bg-[#1d4ed8] font-semibold shadow-sm transition-colors"
        >
          {useDownloadAction ? (
            isDownloading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <FileDown className="mr-2 h-4 w-4" />
                Download PDF
              </>
            )
          ) : isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <FileDown className="mr-2 h-4 w-4" />
              Generate PDF
            </>
          )}
        </Button>
        {(displayContent || showPdfFallback) && (
          <p className="text-xs text-gray-500 text-center mt-2">
            Your download will closely match this preview
          </p>
        )}
      </div>
    </div>
  )
}
